import React, { useEffect, useState } from "react";

import HeadquartersPage from "../../components/headquarters/HeadquartersPage";
import HeadquartersSection from "../../components/headquarters/HeadquartersSection";
import HeadquartersGrid from "../../components/headquarters/HeadquartersGrid";
import HeadquartersMetricCard from "../../components/headquarters/HeadquartersMetricCard";
import StatusPill from "../../components/headquarters/StatusPill";

export default function PlatformBetaControls() {
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [err, setErr] = useState("");
  const [marketingMode, setMarketingMode] = useState("master");
  const [settings, setSettings] = useState({
    workspaceCreationEnabled: false,
    publicSignupEnabled: false,
    requestEarlyAccessEnabled: true,
    betaInviteMode: true,
    showBetaDisclaimer: true,
    marketingCreateWorkspaceAll: false,
    marketingCreateWorkspaceHeader: false,
    marketingCreateWorkspaceHero: false,
    marketingCreateWorkspaceFooter: false,
    betaProgramEnabled: true,
    betaTenantLimit: 5,
    betaWaitlistEnabled: true,
    currentBetaTenants: 0,
  });

  function fromApi(data) {
    return {
      workspaceCreationEnabled: Boolean(data.workspace_creation_enabled),
      publicSignupEnabled: Boolean(data.public_signup_enabled),
      requestEarlyAccessEnabled: Boolean(data.request_early_access_enabled),
      betaInviteMode: Boolean(data.beta_invite_mode),
      showBetaDisclaimer: Boolean(data.show_beta_disclaimer),
      marketingCreateWorkspaceAll: Boolean(data.marketing_create_workspace_all),
      marketingCreateWorkspaceHeader: Boolean(data.marketing_create_workspace_header),
      marketingCreateWorkspaceHero: Boolean(data.marketing_create_workspace_hero),
      marketingCreateWorkspaceFooter: Boolean(data.marketing_create_workspace_footer),
      betaProgramEnabled: data.beta_program_enabled !== undefined ? Boolean(data.beta_program_enabled) : true,
      betaTenantLimit: Number(data.beta_tenant_limit || 5),
      betaWaitlistEnabled: data.beta_waitlist_enabled !== undefined ? Boolean(data.beta_waitlist_enabled) : true,
      currentBetaTenants: Number(data.current_beta_tenants || 0),
    };
  }

  function toApi(nextSettings, nextMarketingMode = marketingMode) {
    return {
      workspace_creation_enabled: nextSettings.workspaceCreationEnabled,
      public_signup_enabled: nextSettings.publicSignupEnabled,
      request_early_access_enabled: nextSettings.requestEarlyAccessEnabled,
      beta_invite_mode: nextSettings.betaInviteMode,
      show_beta_disclaimer: nextSettings.showBetaDisclaimer,
      marketing_buttons_mode: nextMarketingMode,
      marketing_create_workspace_all: nextSettings.marketingCreateWorkspaceAll,
      marketing_create_workspace_header: nextSettings.marketingCreateWorkspaceHeader,
      marketing_create_workspace_hero: nextSettings.marketingCreateWorkspaceHero,
      marketing_create_workspace_footer: nextSettings.marketingCreateWorkspaceFooter,
      beta_program_enabled: nextSettings.betaProgramEnabled,
      beta_tenant_limit: Number(nextSettings.betaTenantLimit || 0),
      beta_waitlist_enabled: nextSettings.betaWaitlistEnabled,
    };
  }

  async function loadSettings() {
    setLoading(true);
    setErr("");

    try {
      const res = await fetch("/api/platform/beta-controls", {
        credentials: "include",
        headers: {
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
      });

      if (!res.ok) {
        throw new Error("Failed to load beta controls.");
      }

      const data = await res.json();
      setSettings(fromApi(data));
      setMarketingMode(data.marketing_buttons_mode || "master");
    } catch (error) {
      setErr(error.message || "Failed to load beta controls.");
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings(nextSettings, nextMarketingMode = marketingMode, key = "settings") {
    setSavingKey(key);
    setErr("");

    try {
      const res = await fetch("/api/platform/beta-controls", {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
        body: JSON.stringify(toApi(nextSettings, nextMarketingMode)),
      });

      if (!res.ok) {
        throw new Error("Failed to save beta controls.");
      }

      const data = await res.json();
      setSettings(fromApi(data));
      setMarketingMode(data.marketing_buttons_mode || nextMarketingMode);
    } catch (error) {
      setErr(error.message || "Failed to save beta controls.");
    } finally {
      setSavingKey("");
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  function updateSetting(key, value) {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next, marketingMode, key);
  }

  function updateIndividualMarketing(key, value) {
    const nextMode = "individual";
    const next = { ...settings, [key]: value };
    setMarketingMode(nextMode);
    setSettings(next);
    saveSettings(next, nextMode, key);
  }

  function updateBetaTenantLimit(value) {
    const cleanValue = Math.max(0, Number(value || 0));
    const next = { ...settings, betaTenantLimit: cleanValue };
    setSettings(next);
    saveSettings(next, marketingMode, "betaTenantLimit");
  }


  const toggleRow = (label, description, checked, onChange) => (
    <div style={toggleCard}>
      <div>
        <div style={{ fontWeight: 800, color: "#ffffff" }}>{label}</div>
        <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 4 }}>
          {description}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
          ...toggleBtn,
          background: checked ? "#e0b84f" : "transparent",
          color: checked ? "#0b1b2b" : "#ffffff",
          borderColor: checked ? "#e0b84f" : "#27496b",
        }}
      >
        {savingKey === label ? "SAVING" : checked ? "ON" : "OFF"}
      </button>
    </div>
  );

  return (
    <HeadquartersPage
      title="Beta Controls"
      subtitle="Control public access, workspace creation, beta enrollment, and marketing-site entry points."
    >
      {err && (
        <HeadquartersSection
          style={{
            border: "1px solid rgba(248,113,113,0.35)",
            background: "rgba(127,29,29,0.25)",
            color: "#fecaca",
          }}
        >
          {err}
        </HeadquartersSection>
      )}

      {loading && (
        <HeadquartersSection>
          Loading beta controls...
        </HeadquartersSection>
      )}

      {!loading && (
        <>
      <HeadquartersSection title="Beta Access Summary">
        <div style={{ marginBottom: 18 }}>
          <StatusPill variant="info" dot>
            Beta Controls V1 Shell
          </StatusPill>
        </div>

        <HeadquartersGrid min={180}>
          <HeadquartersMetricCard
            label="Workspace Creation"
            value={settings.workspaceCreationEnabled ? "ON" : "OFF"}
            subtext="Controls whether new workspaces can be created"
          />
          <HeadquartersMetricCard
            label="Marketing Mode"
            value={marketingMode === "master" ? "Master" : "Individual"}
            subtext="Controls public Create Workspace buttons"
          />
          <HeadquartersMetricCard
            label="Beta Disclaimer"
            value={settings.showBetaDisclaimer ? "ON" : "OFF"}
            subtext="Shows beta notices before public use"
          />
        </HeadquartersGrid>
      </HeadquartersSection>

      <HeadquartersSection
        title="Beta Enrollment Controls"
        subtitle="Control how many beta tenants can enter JobTelesis before signup closes automatically."
      >
        <HeadquartersGrid min={180}>
          <HeadquartersMetricCard
            label="Beta Program"
            value={settings.betaProgramEnabled ? "ON" : "OFF"}
            subtext="Controls whether beta enrollment is open"
          />
          <HeadquartersMetricCard
            label="Maximum Beta Tenants"
            value={settings.betaTenantLimit}
            subtext="Editable beta tenant cap"
          />
          <HeadquartersMetricCard
            label="Current Beta Tenants"
            value={settings.currentBetaTenants}
            subtext="Live count once backend is connected"
          />
          <HeadquartersMetricCard
            label="Remaining Spots"
            value={Math.max(0, Number(settings.betaTenantLimit || 0) - Number(settings.currentBetaTenants || 0))}
            subtext="Automatically closes when this reaches zero"
          />
        </HeadquartersGrid>

        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          {toggleRow(
            "Beta Program Enabled",
            "When OFF, no new beta tenants can enter even if spots are available.",
            settings.betaProgramEnabled,
            (value) => updateSetting("betaProgramEnabled", value)
          )}

          <div style={toggleCard}>
            <div>
              <div style={{ fontWeight: 800, color: "#ffffff" }}>Maximum Beta Tenants</div>
              <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 4 }}>
                Enter 5, 10, 20, or any safe beta cap. Signup closes when the cap is reached.
              </div>
            </div>

            <input
              type="number"
              min="0"
              value={settings.betaTenantLimit}
              onChange={(event) => updateBetaTenantLimit(event.target.value)}
              style={numberInput}
            />
          </div>

          {toggleRow(
            "Beta Waitlist Enabled",
            "When beta is full, visitors can be directed to a waitlist instead of workspace creation.",
            settings.betaWaitlistEnabled,
            (value) => updateSetting("betaWaitlistEnabled", value)
          )}
        </div>
      </HeadquartersSection>

      <HeadquartersSection
        title="Workspace Creation"
        subtitle="Master control for whether new tenants can create workspaces."
      >
        {toggleRow(
          "Workspace Creation Enabled",
          "When OFF, workspace creation should be blocked even if signup links are visible.",
          settings.workspaceCreationEnabled,
          (value) => updateSetting("workspaceCreationEnabled", value)
        )}
      </HeadquartersSection>

      <HeadquartersSection
        title="Marketing Site Controls"
        subtitle="Control Create Workspace buttons on the public JobTelesis marketing site."
      >
        <div style={modeBox}>
          <button
            type="button"
            style={{
              ...modeBtn,
              background: marketingMode === "master" ? "#e0b84f" : "transparent",
              color: marketingMode === "master" ? "#0b1b2b" : "#ffffff",
            }}
            onClick={() => {
              setMarketingMode("master");
              saveSettings(settings, "master", "marketingMode");
            }}
          >
            Master Control
          </button>

          <button
            type="button"
            style={{
              ...modeBtn,
              background: marketingMode === "individual" ? "#e0b84f" : "transparent",
              color: marketingMode === "individual" ? "#0b1b2b" : "#ffffff",
            }}
            onClick={() => {
              setMarketingMode("individual");
              saveSettings(settings, "individual", "marketingMode");
            }}
          >
            Individual Control
          </button>
        </div>

        {toggleRow(
          "All Marketing Create Workspace Buttons",
          "Controls header, hero, and bottom CTA buttons together when Master Control is selected.",
          settings.marketingCreateWorkspaceAll,
          (value) => {
            const nextMode = "master";
            const next = {
              ...settings,
              marketingCreateWorkspaceAll: value,
              marketingCreateWorkspaceHeader: value,
              marketingCreateWorkspaceHero: value,
              marketingCreateWorkspaceFooter: value,
            };
            setMarketingMode(nextMode);
            setSettings(next);
            saveSettings(next, nextMode, "marketingCreateWorkspaceAll");
          }
        )}

        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          {toggleRow(
            "Header Create Workspace",
            "Controls the Create Workspace button in the marketing site top navigation.",
            settings.marketingCreateWorkspaceHeader,
            (value) => updateIndividualMarketing("marketingCreateWorkspaceHeader", value)
          )}

          {toggleRow(
            "Hero Create Workspace",
            "Controls the main Create Workspace button in the landing page hero section.",
            settings.marketingCreateWorkspaceHero,
            (value) => updateIndividualMarketing("marketingCreateWorkspaceHero", value)
          )}

          {toggleRow(
            "Bottom CTA Create Workspace",
            "Controls the Create Workspace button near the bottom call-to-action section.",
            settings.marketingCreateWorkspaceFooter,
            (value) => updateIndividualMarketing("marketingCreateWorkspaceFooter", value)
          )}
        </div>
      </HeadquartersSection>

      <HeadquartersSection
        title="Signup Controls"
        subtitle="Control access to the /signup workspace creation page."
      >
        {toggleRow(
          "Public Signup Page",
          "Controls whether the public signup page should be available.",
          settings.publicSignupEnabled,
          (value) => updateSetting("publicSignupEnabled", value)
        )}
      </HeadquartersSection>

      <HeadquartersSection
        title="Early Access"
        subtitle="Control lead-capture and beta invitation behavior."
      >
        {toggleRow(
          "Request Early Access",
          "Controls whether visitors can request beta access instead of creating a workspace directly.",
          settings.requestEarlyAccessEnabled,
          (value) => updateSetting("requestEarlyAccessEnabled", value)
        )}
      </HeadquartersSection>

      <HeadquartersSection
        title="Beta Settings"
        subtitle="Control beta-mode messaging and future invite-code behavior."
      >
        <div style={{ display: "grid", gap: 12 }}>
          {toggleRow(
            "Beta Invite Mode",
            "Future control for requiring invite codes before workspace creation.",
            settings.betaInviteMode,
            (value) => updateSetting("betaInviteMode", value)
          )}

          {toggleRow(
            "Show Beta Disclaimer",
            "Shows beta disclaimer language on public signup and beta entry points.",
            settings.showBetaDisclaimer,
            (value) => updateSetting("showBetaDisclaimer", value)
          )}
        </div>
      </HeadquartersSection>
        </>
      )}
    </HeadquartersPage>
  );
}

const toggleCard = {
  border: "1px solid rgba(255,255,255,0.08)",
  background: "#10263a",
  borderRadius: 14,
  padding: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

const toggleBtn = {
  minWidth: 78,
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid #27496b",
  fontWeight: 900,
  cursor: "pointer",
};

const numberInput = {
  width: 110,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #27496b",
  background: "#071827",
  color: "#ffffff",
  fontWeight: 900,
  fontSize: 16,
};

const modeBox = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 14,
};

const modeBtn = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #27496b",
  fontWeight: 800,
  cursor: "pointer",
};
